import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api.service';
import LoginLayout from '../components/LoginLayout';

const inputStyle = {
  width: '100%',
  height: '42px',
  padding: '0 14px',
  fontSize: '0.9rem',
  color: '#1a1a1a',
  backgroundColor: '#f8f9fa',
  border: '1px solid #ddd',
  borderRadius: '8px',
  outline: 'none',
  transition: 'border-color 0.15s',
  colorScheme: 'light',
  WebkitAppearance: 'none',
};

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const blobToDataUrl = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await ApiService.login(username, password);

      if (response.data && response.data.success) {
        const user = response.data?.data?.user || response.data?.user || response.data?.data?.data?.user;
        console.log('✅ [Login] Login successful:', user.username);

        const logoPath = user?.logoUrl || user?.logo_url || '';
        let logoDataUrl = '';
        if (logoPath) {
          try {
            const logoRes = await ApiService.downloadFile(logoPath);
            if (logoRes?.data) {
              logoDataUrl = await blobToDataUrl(logoRes.data);
            }
          } catch (downloadErr) {
            console.error('❌ [Login] Failed to download logo:', downloadErr);
          }
        }

        onLogin({
          id: user.id,
          role: user.role,
          username: user.username,
          name: user.fullName || user.full_name,
          email: user.email,
          typingCenterId: user.typingCenterId || user.typing_center_id,
          typingCenterName: user.typingCenterName || user.typing_center_name,
          // Thaklis-features toggle + TC type drive which Thaklis-only UI is shown.
          thaklisEnabled: user.thaklisEnabled !== undefined ? user.thaklisEnabled
            : (user.thaklis_enabled !== undefined ? user.thaklis_enabled : true),
          organizationType: user.organizationType || user.organization_type || null,
          // Promoted (from a B2B's customer TC) → show the "Open my B2B portal" auto-login action.
          isPromoted: user.isPromoted === true || user.is_promoted === true,
          // B2B connections feature gate: promoted TCs, plus independent TCs a platform admin granted it.
          connectionsEnabled: user.connectionsEnabled === true || user.connections_enabled === true,
          logoUrl: logoPath,
          logoDataUrl,
          profileImageUrl: user.profileImageUrl || user.profile_image_url || '',
          active: user.active,
          // Resolved RBAC matrix ({feature: {read, write, delete}}) — gates sidebar/routes/buttons.
          permissions: user.permissions || {}
        });

        navigate('/dashboard');
      }
    } catch (err) {
      console.error('❌ [Login] Login failed:', err);
      const errorMessage = err.response?.data?.message || 'Invalid credentials. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginLayout
      title="Management Portal"
      footerLink={{ text: "Customer? Login to portal", href: "/portal/login" }}
    >
      <div style={{
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        padding: '28px 28px 32px',
        colorScheme: 'light',
      }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1a1a1a', marginBottom: '20px' }}>
          Sign In
        </h2>

        {error && (
          <div style={{
            padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.85rem',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#555', marginBottom: '6px' }}>
              Username
            </label>
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#999'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#555', marginBottom: '6px' }}>
              Password
            </label>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#999'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              height: '44px',
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#ffffff',
              backgroundColor: loading ? '#999' : '#1a1a1a',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = '#333'; }}
            onMouseLeave={(e) => { if (!loading) e.target.style.backgroundColor = '#1a1a1a'; }}
          >
            {loading ? 'Signing in...' : 'Login'}
          </button>
        </form>
      </div>
    </LoginLayout>
  );
};

export default Login;
